import { useMemo } from 'react'

import { type CardRefType } from '@/defs/card'
import { type ClipboardCardStatusType } from '@/defs/clipboard'
import { clipboardActions } from '@/store/clipboard'
import { useReduxDispatch, useReduxSelector } from '@/store/hooks'
import { selectors } from '@/store/selectors'

type Props = {
  cardRefs: CardRefType[]
}

export const useClipboardButton = ({ cardRefs: cardRefsInput }: Props) => {
  const dispatch = useReduxDispatch()

  const refsKey = cardRefsInput.map(({ cardId }) => cardId).join(',')
  const cardRefs = useMemo(
    () => cardRefsInput,
    // NOTE: refsKey serves the purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refsKey],
  )

  const savedCardIdsEncoded = useReduxSelector((s) => selectors.clipboardCardIdsEncoded(s, cardRefs))

  return useMemo(() => {
    const cardsInClipboard = cardRefs.map<CardRefType & { status: ClipboardCardStatusType }>((cardRef, i) => ({
      ...cardRef,
      status: savedCardIdsEncoded[i] === '1' ? 'saved' : 'unsaved',
    }))
    const isAllInClipboard = cardsInClipboard.every((c) => c.status === 'saved')
    const isSomeInClipboard = !isAllInClipboard && cardsInClipboard.some((c) => c.status === 'saved')

    const onToggleAllClick = () =>
      dispatch(isAllInClipboard ? clipboardActions.remove(cardRefs) : clipboardActions.add(cardRefs))

    return { cardsInClipboard, isAllInClipboard, isSomeInClipboard, onToggleAllClick }
  }, [cardRefs, savedCardIdsEncoded, dispatch])
}
